import React from 'react';
import { useIntl } from 'react-intl';

import { FieldGroup, Notification, Select } from '@xsolla/ui-core';

import { PROJECT_LINKS } from '../../../constants';
import { CommonLink, ShortSettingsBlock } from '../../../styled';
import { Notice } from '../../ui';
import {
  LocaleOptionWrapper,
  NotificationWrapper,
  WidgetSettingsDescription,
  WidgetSettingsTitle,
  WidgetSettingsWrapper
} from './styles';
import { Action, ActionType, WidgetCustomizationState } from './types';

type Props = {
  state: WidgetCustomizationState;
  dispatch: React.Dispatch<Action>;
};

export const WidgetSettings = ({ state, dispatch }: Props): JSX.Element => {
  const { countryOptions, languageOptions, language, country } = state.widgetPreviewSettings;
  const { oauth2Options, oauth2Id } = state.oauth2;
  const intl = useIntl();

  return (
    <WidgetSettingsWrapper>
      <ShortSettingsBlock>
        <WidgetSettingsTitle>
          {intl.messages['widget-customization.settings.oauth.title']}
        </WidgetSettingsTitle>
        <WidgetSettingsDescription>
          {intl.messages['widget-customization.settings.oauth.description']}
        </WidgetSettingsDescription>
        <FieldGroup
          label={intl.messages['widget-customization.settings.provided-oauth.title']}
          size="md">
          <Select
            errorMessage=""
            onBlur={() => {}}
            onFocus={() => {}}
            onChange={(value: string) => dispatch({ type: ActionType.setOauth2Id, payload: value })}
            options={oauth2Options || []}
            disabled={oauth2Options.length === 0}
            value={oauth2Id}
            data-test-id="wc_select-oauth"
            search={{
              emptyMessage: intl.formatMessage({ id: 'app.multi-select.empty' }),
              placeholder: intl.formatMessage({ id: 'app.multi-select.search' })
            }}
            size="md"
          />
        </FieldGroup>
        <WidgetSettingsTitle>
          {intl.messages['widget-customization.settings.language.title']}
        </WidgetSettingsTitle>
        <WidgetSettingsDescription>
          {intl.messages['widget-customization.settings.language.description']}
        </WidgetSettingsDescription>
        <NotificationWrapper>
          <Notification icon={Notice}>
            {intl.formatMessage(
              { id: 'widget-customization.settings.language.notification' },
              {
                br: () => <br />,
                o: (chunk) => <LocaleOptionWrapper>{chunk}</LocaleOptionWrapper>
              }
            )}
          </Notification>
        </NotificationWrapper>
        <FieldGroup
          label={intl.messages['widget-customization.settings.provided-language.title']}
          footer={intl.formatMessage(
            {
              id: 'widget-customization.settings.provided-language.description'
            },
            {
              a: (chunk: string) => (
                <CommonLink href={PROJECT_LINKS.WIDGET_LANGUAGES} target="_blank">
                  {chunk}
                </CommonLink>
              )
            }
          )}
          size="md">
          <Select
            errorMessage=""
            onBlur={() => {}}
            onFocus={() => {}}
            onChange={(value: string) => dispatch({ type: ActionType.setLanguage, payload: value })}
            options={languageOptions}
            value={language}
            data-test-id="wc_select-language"
            search={{
              emptyMessage: intl.formatMessage({ id: 'app.multi-select.empty' }),
              placeholder: intl.formatMessage({ id: 'app.multi-select.search' })
            }}
            size="md"
          />
        </FieldGroup>
        <WidgetSettingsTitle>
          {intl.messages['widget-customization.settings.country.title']}
        </WidgetSettingsTitle>
        <WidgetSettingsDescription>
          {intl.messages['widget-customization.settings.country.description']}
        </WidgetSettingsDescription>
        {/* ToDo: uncomment when "social ranking" implemented */}
        {/* <NotificationWrapper> */}
        {/*   <Notification icon={Notice}> */}
        {/*     {intl.formatMessage( */}
        {/*       { id: 'widget-customization.settings.country.notification' }, */}
        {/*       { */}
        {/*         a: (chunk: string) => <NotifyLink href="/">{chunk}</NotifyLink> */}
        {/*       } */}
        {/*     )} */}
        {/*   </Notification> */}
        {/* </NotificationWrapper> */}
        <FieldGroup label={intl.messages['widget-customization.settings.country.title']} size="md">
          <Select
            errorMessage=""
            onBlur={() => {}}
            onFocus={() => {}}
            onChange={(value: string) => dispatch({ type: ActionType.setCountry, payload: value })}
            options={countryOptions}
            value={country}
            data-test-id="wc_select-country"
            search={{
              emptyMessage: intl.formatMessage({ id: 'app.multi-select.empty' }),
              placeholder: intl.formatMessage({ id: 'app.multi-select.search' })
            }}
            size="md"
          />
        </FieldGroup>
      </ShortSettingsBlock>
    </WidgetSettingsWrapper>
  );
};
